import { css, cx } from "styled-system/css";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { DashboardEmpty } from "@/components/dashboard/dashboard-empty";
import { Button } from "@/components/ui/button";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { CourtFormModal, type CourtRow } from "@/components/dashboard/court-form-modal";

const SURFACE_LABELS: Record<string, string> = {
  GRASS: "Césped natural",
  SYNTHETIC: "Sintético",
  CLAY: "Tierra batida",
  HARD: "Dura",
  WOOD: "Madera",
  OTHER: "Otra",
};

function CourtListRow({ court, onEdit, onSaved }: { court: CourtRow; onEdit: () => void; onSaved: () => void }) {
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);

  async function toggleActive() {
    setBusy(true);
    await fetchWithSessionSync(
      `/api/dashboard/courts/${court.id}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ isActive: !court.isActive }),
      },
      refresh,
    );
    setBusy(false);
    onSaved();
  }

  return (
    <li
      className={cx(
        cf.dashSurface,
        css({
          p: "4",
          display: "flex",
          flexDir: { base: "column", sm: "row" },
          justifyContent: "space-between",
          gap: { base: "4", sm: "3" },
          opacity: court.isActive ? 1 : 0.5,
        }),
      )}
      style={court.color ? { borderLeftWidth: "4px", borderLeftColor: court.color } : {}}
    >
      <div className={css({ minW: 0 })}>
        <p className={css({ fontWeight: "semibold", color: "#0f172a" })}>
          {court.name}
          {!court.isActive && <span className={css({ ml: "2", fontSize: "xs", color: "#94a3b8" })}>(inactiva)</span>}
        </p>
        <p className={css({ fontSize: "sm", color: "#64748b", mt: "1" })}>
          {court.sport && `${court.sport} · `}
          {SURFACE_LABELS[court.surface] ?? court.surface}
          {` · Hasta ${court.capacity} jugadores`}
        </p>
        {court.description && <p className={css({ fontSize: "sm", color: "#94a3b8", mt: "1" })}>{court.description}</p>}
      </div>
      <div
        className={css({
          display: "flex",
          flexDir: { base: "column", sm: "row" },
          gap: { base: "2.5", sm: "3" },
          alignItems: { base: "stretch", sm: "center" },
          flexShrink: 0,
        })}
      >
        <span
          className={css({
            fontSize: "sm",
            color: "#334155",
            whiteSpace: "nowrap",
            textAlign: { base: "left", sm: "right" },
          })}
        >
          {court.durationMinutes} min/turno
          {court.pricePerSlot != null && ` · $${court.pricePerSlot}`}
        </span>
        <div className={css({ display: "flex", gap: "2" })}>
          <Button
            variant="secondary"
            size="sm"
            onClick={onEdit}
            className={css({ flex: { base: "1", sm: "initial" }, h: { base: "11", sm: "auto" } })}
          >
            Editar
          </Button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void toggleActive()}
            className={css({
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: { base: "1", sm: "initial" },
              h: { base: "11", sm: "auto" },
              fontSize: "xs",
              px: "3",
              py: "1.5",
              rounded: "md",
              borderWidth: "1px",
              borderColor: court.isActive ? "#fca5a5" : "#86efac",
              color: court.isActive ? "#dc2626" : "#16a34a",
              bg: "white",
              cursor: "pointer",
              whiteSpace: "nowrap",
              _hover: { bg: court.isActive ? "#fef2f2" : "#f0fdf4" },
            })}
          >
            {court.isActive ? "Desactivar" : "Activar"}
          </button>
        </div>
      </div>
    </li>
  );
}

export function DashboardCourts() {
  const { refresh } = useAuth();
  const [courts, setCourts] = useState<CourtRow[] | null>(null);
  const [noFacility, setNoFacility] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CourtRow | null>(null);

  const load = useCallback(async () => {
    const r = await fetchWithSessionSync("/api/dashboard/courts", { credentials: "include" }, refresh);
    if (r.status === 403) {
      setNoFacility(true);
      return;
    }
    if (!r.ok) return;
    const data = (await r.json()) as { courts: CourtRow[] };
    setCourts(data.courts);
  }, [refresh]);

  useEffect(() => {
    void load();
  }, [load]);

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(c: CourtRow) {
    setEditing(c);
    setModalOpen(true);
  }

  if (noFacility) {
    return (
      <DashboardPageShell
        eyebrow="Instalación"
        title="Canchas"
        description="Esta sección es exclusiva para cuentas de tipo instalación deportiva."
      >
        <p className={css({ fontSize: "sm", color: "#64748b" })}>Tu cuenta no está configurada como instalación.</p>
      </DashboardPageShell>
    );
  }

  return (
    <DashboardPageShell
      eyebrow="Instalación"
      title="Canchas"
      description="Administrá las canchas de tu instalación. Los clientes eligen una al reservar online."
    >
      <div className={css({ display: "flex", justifyContent: "flex-end", mb: "5" })}>
        <Button variant="primary" onClick={openCreate} className={css({ w: { base: "full", sm: "auto" } })}>
          + Agregar cancha
        </Button>
      </div>

      {courts === null ? (
        <LoadingFrame variant="light" minHeight={280} />
      ) : courts.length === 0 ? (
        <DashboardEmpty title="Todavía no cargaste canchas" hint="Usá el botón de arriba para agregar la primera cancha." />
      ) : (
        <ul className={css({ display: "flex", flexDir: "column", gap: "3" })}>
          {courts.map((c) => (
            <CourtListRow key={c.id} court={c} onEdit={() => openEdit(c)} onSaved={() => void load()} />
          ))}
        </ul>
      )}

      <CourtFormModal open={modalOpen} onClose={() => setModalOpen(false)} court={editing} onSaved={() => void load()} />
    </DashboardPageShell>
  );
}
